import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import { isDeferredSettlement } from "../utils/deferredSettlement";
// src/pages/SaleEdit.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet, apiPut } from "../api/client";
import { ArrowLeftIcon, XMarkIcon, TrashIcon } from "@heroicons/react/24/solid";
import { AppleComboBox } from "../components/AppleComboBox";
import { companyRoute } from "../utils/routeHelpers";
import { AuthMe } from "../types/auth";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { SaleInfoHeader } from "../components/SaleInfoHeader";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";
import { AppleCard } from "../components/AppleCard";
import { AppleInput } from "../components/AppleInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import MemoSection from "../components/MemoSection";
import { IconButton } from "../components/IconButton";
import { AppleDateInput } from "../components/AppleDateInput";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
} from "../utils/closedPeriod";

/* =========================
   Types
   ========================= */
interface ComboItem {
  id: string | number;
  name: string;
}
interface Customer extends ComboItem {
  email?: string;
}
interface Product extends ComboItem {
  sell_price: number;
  stock?: number;
}
interface PaymentMethod extends ComboItem {
  type?: string;
}
interface SaleLine {
  id?: number;
  product_id?: string | number;
  detail?: string;
  qty: string; // editable string
  price: string; // editable string
  discount: string; // percent as string
  total: string; // editable string
  lastEdited?: "price" | "discount" | "total";
  item_kind?: "product" | "service";
  preorder?: boolean;
  fulfilled_date?: string | null;
}
interface Invoice {
  id: number;
  invoice_number: string;
}
interface Sale {
  on_account: boolean;
  id: number;
  date: string;
  customer: Customer | null;
  amount: number;
  status: string | null;
  payment_method_id: number | null;
  due_date?: string;
  lines: Array<{
    id?: number;
    product_id?: number | string | null;
    unit_price?: number | string;
    qty?: number | string;
    discount?: number | string; // PERCENT
    total?: number | string;
    detail?: string;
    preorder?: boolean;
    fulfilled_date?: string | null;
  }>;
  memo?: string;
  invoice?: Invoice | null;
  edited?: boolean;
  voided?: boolean;
  last_edited_at?: string;
  amount_paid?: number;
}
type BubbleType =
  | "preorder"
  | "fulfilled"
  | "paid"
  | "partial"
  | "unpaid"
  | "voided";
type BubbleInfo = { label: string; bubbleType: BubbleType };

type Editability = {
  allowed: boolean;
  reasons: string[];
  editable_until?: string | null;
  pro_override?: boolean;
};

/* =========================
   Helpers
   ========================= */
function moneyAny(n: number | string | undefined | null) {
  const num = typeof n === "string" ? Number(n) : n;
  return typeof num === "number" && Number.isFinite(num)
    ? `$${formatNumber(num)}`
    : "—";
}

const round2 = (n: number) => Math.round(n * 100) / 100;

function getLineTotal(qty: string, price: string, discountPct: string) {
  const q = parseFloat(qty) || 1;
  const p = parseFloat(price) || 0;
  const d = parseFloat(discountPct) || 0;

  const n = Math.round(q * p * (1 - d / 100) * 100) / 100;
  if (!Number.isFinite(n)) return "0";
  return String(n);
}

// money helpers
function to2dpStr(n: number | string): string {
  const x = typeof n === "string" ? Number(n) : n;
  if (!isFinite(x)) return "0.00";
  return (Math.round(x * 100) / 100).toFixed(2);
}

// numeric input helpers
const sanitizeNumeric = (v: string) => {
  let s = v.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
};
const clamp2dpStr = (v: string) => {
  if (v === "" || v === ".") return "";
  const n = Number(v);
  if (!isFinite(n)) return "";
  return n.toFixed(2);
};
const clampPctStr = (v: string) => {
  if (v === "" || v === ".") return "";
  let n = Number(v);
  if (!isFinite(n)) return "";
  if (n < 0) n = 0;
  if (n > 100) n = 100;
  return (Math.round(n * 100) / 100).toString();
};
// qty: keep up to 2dp; min 1
const normalizeQtyStr = (v: string) => {
  if (v === "" || v === ".") return "1";
  let n = Number(v);
  if (!isFinite(n) || n <= 0) n = 1;
  const s = String(v);
  if (s.includes(".")) return (Math.round(n * 100) / 100).toString();
  return String(Math.trunc(n));
};

// Errors → friendly string
function toErrorString(err: any) {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    return d
      .map((e: any) => {
        const loc = Array.isArray(e.loc) ? e.loc.join(".") : "field";
        return `${loc}: ${e.msg}`;
      })
      .join("\n");
  }
  if (typeof d === "string") return d;
  return (err instanceof LocalSalesError ? err.display : err?.message) || localMessage("sales.errors.saveEdit");
}

// Bubbles (same logic as detail page)
function getDetailBubbles(sale: Sale | null, totalPaid: number): BubbleInfo[] {
  if (!sale) return [];
  if (sale.voided) return [{ label: "Voided", bubbleType: "voided" }];

  const isPreorder = sale.lines?.some((l) => !!l.preorder) && !sale.voided;
  const isFulfilled =
    isPreorder && sale.lines?.some((l) => l.preorder && !!l.fulfilled_date);
  const s = sale.status?.toLowerCase() || "";
  const isPaid =
    s === "paid" ||
    s === "closed" ||
    Math.abs((totalPaid || 0) - (sale.amount || 0)) < 0.005;
  const isPartial = totalPaid > 0 && !isPaid;

  if (!isPreorder) {
    if (isPaid) return [{ label: "Paid", bubbleType: "paid" }];
    if (isPartial) return [{ label: "Partial Paid", bubbleType: "partial" }];
    return [{ label: "Unpaid", bubbleType: "unpaid" }];
  }
  if (!isFulfilled) {
    if (isPaid)
      return [
        { label: "Preorder", bubbleType: "preorder" },
        { label: "Paid", bubbleType: "paid" },
      ];
    if (isPartial)
      return [
        { label: "Preorder", bubbleType: "preorder" },
        { label: "Partial Paid", bubbleType: "partial" },
      ];
    return [
      { label: "Preorder", bubbleType: "preorder" },
      { label: "Unpaid", bubbleType: "unpaid" },
    ];
  }
  if (isPaid)
    return [
      { label: "Fulfilled", bubbleType: "fulfilled" },
      { label: "Paid", bubbleType: "paid" },
    ];
  if (isPartial)
    return [
      { label: "Fulfilled", bubbleType: "fulfilled" },
      { label: "Partial Paid", bubbleType: "partial" },
    ];
  return [
    { label: "Fulfilled", bubbleType: "fulfilled" },
    { label: "Unpaid", bubbleType: "unpaid" },
  ];
}

/* =========================
   Component
   ========================= */
type LocalSalesMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null };
type SalesMessage = string | LocalSalesMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalSalesMessage => ({ key, values });
class LocalSalesError extends Error {
  constructor(readonly display: LocalSalesMessage) { super(display.key); }
}
function localApiDetail(err: any): SalesMessage {
  if (err instanceof LocalSalesError) return err.display;
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalSalesMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

export default function SaleEdit() {
  const { t } = useTranslation();
  const messageText = (message: SalesMessage | null): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : message.values;
    return t(message.key, values ?? {});
  };
  const { id, company_id } = useParams<{ id: string; company_id: string }>();
  const navigate = useNavigate();

  const [sale, setSale] = useState<Sale | null>(null);
  const [form, setForm] = useState<any>(null);
  // True once the user edits the due-date field, so an untouched save never
  // repopulates a stored NULL (see the submit handler + AppleDateInput below).
  const dueDateDirtyRef = useRef(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);

  const selectedCustomer = useMemo(() => {
    const cid = form?.customer_id;
    if (!cid) return null;
    return customers.find((c) => String(c.id) === String(cid)) ?? null;
  }, [customers, form?.customer_id]);

  // Split errors: load vs save
  const [loadError, setLoadError] = useState<SalesMessage | null>(null);
  const [saveError, setSaveError] = useState<SalesMessage | null>(null);

  const [saving, setSaving] = useState(false);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );

  // ✅ Memo (PurchaseEdit behavior): always editable draft, saved only on Enter
  const [memoDraft, setMemoDraft] = useState("");
  const locked = false;

  // payments (for outstanding)
  const [paidTotal, setPaidTotal] = useState<number>(0);

  // ---- Preflight editability check (simple + race-free) ----
  useEffect(() => {
    if (!id || !company_id) return;
    (async () => {
      try {
        const [ed, me] = await Promise.all([
          apiGet<Editability>(
            `/sales/${id}/editability?company_id=${company_id}`,
          ),
          apiGet<AuthMe>("/auth/me"),
        ]);

        const proOverride = !!me?.is_user_pro && ed?.pro_override === true;
        const allowed = (ed?.allowed ?? true) || proOverride;

        if (!allowed) {
          navigate(companyRoute(company_id!, `/sales/${id}`), {
            replace: true,
            state: {
              editBlocked: {
                reasons: ed?.reasons,
                editable_until: ed?.editable_until,
                pro_override: ed?.pro_override,
              },
            },
          });
        }
      } catch {
        // fail-closed; server still enforces on PUT
      }
    })();
  }, [id, company_id, navigate]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // ---- Load data for editing ----
  useEffect(() => {
    if (!id || !company_id) return;

    setLoading(true);
    setLoadError(null);
    setSaveError(null);

    Promise.all([
      apiGet(`/sales/${id}?company_id=${company_id}`),
      apiGet<Customer[]>(`/customers?company_id=${company_id}`),
      apiGet<Product[]>(`/products?company_id=${company_id}`),
      apiGet<PaymentMethod[]>(`/payment-methods?company_id=${company_id}`),
    ])
      .then(
        ([saleDataRaw, customersData, productsData, paymentMethodsData]) => {
          const saleData = saleDataRaw as Sale;

          setSale(saleData);
          setCustomers(customersData);
          setProducts(productsData);
          setPaymentMethods(paymentMethodsData);

          // ✅ initialize memo draft from loaded sale
          setMemoDraft(saleData.memo ?? "");

          // Lines: backend discount is PERCENT (5 means 5)
          const lines: SaleLine[] = (saleData.lines || []).map((line: any) => {
            const qty = Number(line.qty || 1);
            const unit = Number(line.unit_price || 0);
            const discPct = clampPctStr(String(line.discount ?? "0")) || "0";
            const total =
              line.total !== null &&
              line.total !== undefined &&
              String(line.total).trim() !== ""
                ? String(line.total)
                : getLineTotal(String(qty), String(unit), discPct);
            return {
              id: line.id,
              product_id: line.product_id ? String(line.product_id) : "",
              detail: line.detail ?? "",
              qty: String(qty || 1),
              price: String(unit || 0),
              discount: String(discPct),
              total,
              item_kind: line.product_id ? "product" : "service",
              preorder: !!line.preorder,
              fulfilled_date: line.fulfilled_date ?? null,
            };
          });

          setForm({
            customer_id: saleData.customer?.id
              ? String(saleData.customer.id)
              : "",
            payment_method_id:
              saleData.payment_method_id !== null &&
              saleData.payment_method_id !== undefined
                ? String(saleData.payment_method_id)
                : "",
            due_date: saleData.due_date ?? "",
            date: saleData.date ?? "",
            // memo is handled by memoDraft (PurchaseEdit behavior)
            lines,
          });

          // Posted payments come from the same sale response as the list/detail.
          setPaidTotal(Number(saleData.amount_paid || 0));
        },
      )
      .catch(() => setLoadError(localMessage("sales.errors.loadEdit")))
      .finally(() => setLoading(false));
  }, [id, company_id]);

  // ---- Line change handlers ----
  const handleLineChange = (
    idx: number,
    field: "qty" | "price" | "discount" | "total" | keyof SaleLine,
    value: string,
  ) => {
    const raw = sanitizeNumeric(value);

    setForm((prev: any) => {
      const newLines = prev.lines.map((line: SaleLine, i: number) => {
        if (i !== idx) return line;

        let qty = line.qty;
        let price = line.price;
        let discount = line.discount;
        let total = line.total;
        let detail = line.detail ?? "";

        const lastEdited: SaleLine["lastEdited"] =
          field === "price" || field === "discount" || field === "total"
            ? field
            : line.lastEdited;

        if (field === "qty") qty = raw;
        if (field === "price") price = raw;
        if (field === "discount") discount = raw;
        if (field === "total") total = raw;
        if (field === "detail") detail = value;

        if (field === "qty" || field === "price" || field === "discount") {
          total = getLineTotal(qty, price, discount);
        } else if (field === "total") {
          const q = parseFloat(qty);
          const d = parseFloat(discount);
          const t = parseFloat(total);

          if (
            Number.isFinite(q) &&
            q > 0 &&
            Number.isFinite(d) &&
            Number.isFinite(t) &&
            t >= 0
          ) {
            const discountFactor = 1 - d / 100;

            if (discountFactor > 0) {
              const unit = Math.round((t / q / discountFactor) * 100) / 100;

              if (Number.isFinite(unit) && unit >= 0) {
                price = String(unit);
              }
            }
          }
        }

        return { ...line, qty, price, discount, total, detail, lastEdited };
      });

      return { ...prev, lines: newLines };
    });
  };

  // ---- Blur formatters ----
  const handleQtyBlur = (idx: number, value: string) => {
    const v = normalizeQtyStr(sanitizeNumeric(value));
    setForm((prev: any) => {
      const lines = [...prev.lines];
      lines[idx] = {
        ...lines[idx],
        qty: v,
        total: getLineTotal(v, lines[idx].price, lines[idx].discount),
      };
      return { ...prev, lines };
    });
  };
  const handlePriceBlur = (idx: number, value: string) => {
    const v = sanitizeNumeric(value);
    const clean = v === "" || v === "." ? "0" : v;

    setForm((prev: any) => {
      const lines = [...prev.lines];
      lines[idx] = {
        ...lines[idx],
        price: clean,
        total: getLineTotal(lines[idx].qty, clean, lines[idx].discount),
      };
      return { ...prev, lines };
    });
  };
  const handleDiscountBlur = (idx: number, value: string) => {
    const v = clampPctStr(sanitizeNumeric(value)) || "0";
    setForm((prev: any) => {
      const lines = [...prev.lines];
      lines[idx] = {
        ...lines[idx],
        discount: v,
        total: getLineTotal(lines[idx].qty, lines[idx].price, v),
      };
      return { ...prev, lines };
    });
  };
  const handleTotalBlur = (idx: number, value: string) => {
    const v = sanitizeNumeric(value);
    const clean = v === "" || v === "." ? "0" : v;

    setForm((prev: any) => {
      const lines = [...prev.lines];
      const q = parseFloat(lines[idx].qty);
      const d = parseFloat(lines[idx].discount);
      const t = parseFloat(clean);
      let nextPrice = lines[idx].price;

      if (
        Number.isFinite(q) &&
        q > 0 &&
        Number.isFinite(d) &&
        Number.isFinite(t) &&
        t >= 0
      ) {
        const discountFactor = 1 - d / 100;

        if (discountFactor > 0) {
          const unit = Math.round((t / q / discountFactor) * 100) / 100;

          if (Number.isFinite(unit) && unit >= 0) {
            nextPrice = String(unit);
          }
        }
      }

      lines[idx] = {
        ...lines[idx],
        total: clean,
        price: nextPrice,
        lastEdited: "total",
      };
      return { ...prev, lines };
    });
  };

  const handleLineProductChange = (idx: number, value: string) => {
    setForm((prev: any) => {
      const newLines = [...prev.lines];
      if (!value) {
        newLines[idx] = {
          ...newLines[idx],
          product_id: "",
          price: "0",
          item_kind: "service",
          detail: newLines[idx].detail ?? "",
          total: getLineTotal(newLines[idx].qty, "0", newLines[idx].discount),
        };
      } else {
        const prod = products.find((p) => String(p.id) === value);
        const price = prod ? String(prod.sell_price) : "0";
        newLines[idx] = {
          ...newLines[idx],
          product_id: value,
          price,
          item_kind: "product",
          detail: "",
          total: getLineTotal(newLines[idx].qty, price, newLines[idx].discount),
        };
      }
      return { ...prev, lines: newLines };
    });
  };

  const handleLineRemove = (idx: number) => {
    setForm((prev: any) => ({
      ...prev,
      lines: prev.lines.filter((_: any, i: number) => i !== idx),
    }));
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const selectedPayment = useMemo(
    () =>
      paymentMethods.find(
        (a) => String(a.id) === String(form?.payment_method_id),
      ),
    [paymentMethods, form?.payment_method_id],
  );

  const isOnAccount = useMemo(
    () => selectedPayment && String(selectedPayment.id) !== String(sale?.payment_method_id)
      ? isDeferredSettlement(selectedPayment.type)
      : !!sale?.on_account,
    [selectedPayment, sale?.payment_method_id, sale?.on_account],
  );

  const productOptions = useMemo(
    () => [
      { id: "", name: "Service", rawName: "Service", stock: undefined },
      ...products.map((p) => ({
        id: String(p.id),
        rawName: p.name,
        stock: p.stock,
        name:
          typeof p.stock !== "undefined"
            ? `${p.name} (Stock: ${p.stock})`
            : p.name,
      })),
    ],
    [products],
  );

  // ✅ Live total from edited lines
  const computedTotal = useMemo(() => {
    if (!form?.lines?.reduce) return 0;
    const sum = form.lines.reduce(
      (acc: number, line: SaleLine) => acc + (parseFloat(line.total) || 0),
      0,
    );
    return round2(sum);
  }, [form?.lines]);

  // ✅ Live outstanding from edited total - paidTotal
  const computedOutstanding = useMemo(() => {
    const out = round2((computedTotal || 0) - (paidTotal || 0));
    return out < 0 ? 0 : out;
  }, [computedTotal, paidTotal]);

  const bubbles: BubbleInfo[] = getDetailBubbles(sale, paidTotal);

  // ---- Submit ----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sale || !form) return;

    // Due-date floor (consistent with the create/purchase forms): a present,
    // user-set on-account due date may not be earlier than the sale date. A
    // cleared field (deliberate "no due date") and an untouched field are
    // exempt -- they are handled by the omit/null logic in the payload below.
    if (
      isOnAccount &&
      dueDateDirtyRef.current &&
      form.due_date &&
      form.date &&
      form.due_date < form.date
    ) {
      setSaveError(localMessage("sales.errors.editDueDate", { date: form.date }));
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // normalize lines before submit
      const linesPayload = (form.lines as SaleLine[]).map((line) => {
        const qtyStr = normalizeQtyStr(sanitizeNumeric(line.qty));
        const priceStr = to2dpStr(sanitizeNumeric(line.price));
        const totalStr = to2dpStr(sanitizeNumeric(line.total));
        const discPctStr = clampPctStr(sanitizeNumeric(line.discount));

        const qtyNum = Number(qtyStr);
        const unitPriceNum = Number(priceStr);
        const totalNum = Number(totalStr);
        const discNum = Number(discPctStr);

        if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
          throw new LocalSalesError(localMessage("sales.errors.invalidQuantity"));
        }
        if (!Number.isFinite(unitPriceNum) || unitPriceNum < 0) {
          throw new LocalSalesError(localMessage("sales.errors.invalidUnitPrice"));
        }
        if (!Number.isFinite(totalNum) || totalNum < 0) {
          throw new LocalSalesError(localMessage("sales.errors.invalidTotal"));
        }
        if (!Number.isFinite(discNum) || discNum < 0 || discNum > 100) {
          throw new LocalSalesError(localMessage("sales.errors.invalidDiscount"));
        }

        const discountToSend = discNum;

        const itemKind =
          line.item_kind ?? (line.product_id ? "product" : "service");

        const base: any = {
          id: line.id,
          qty: qtyNum,
          unit_price: unitPriceNum,
          discount: discountToSend,
          total: totalNum,
          item_kind: itemKind,
          preorder: !!line.preorder,
          detail: itemKind === "service" ? (line.detail ?? "").trim() : "",
        };

        if (line.product_id) base.product_id = Number(line.product_id);
        else base.product_id = undefined;

        if (line.preorder) base.fulfilled_date = line.fulfilled_date ?? null;
        return base;
      });

      const payload: any = {
        lines: linesPayload,
        // ✅ PurchaseEdit behavior: save memoDraft only on Enter
        memo: memoDraft,
        customer_id: form.customer_id ? Number(form.customer_id) : null,
        payment_method_id: form.payment_method_id
          ? Number(form.payment_method_id)
          : null,
        date: form.date || undefined,
      };
      // Due-date policy (edit): only send due_date when the user actually
      // touched the field. Untouched -> omit -> the backend leaves the stored
      // value untouched (a historical NULL is never repopulated). Touched ->
      // send the chosen date, or an explicit null on a deliberate clear.
      if (isOnAccount && dueDateDirtyRef.current) {
        payload.due_date = form.due_date ? form.due_date : null;
      }

      await apiPut(`/sales/${id}?company_id=${company_id}`, payload);
      navigate(companyRoute(company_id!, `/sales/${id}`));
    } catch (err: any) {
      if (isClosedPeriodError(err)) {
        setSaveError(localClosedPeriod(closedThroughDate));
        return;
      }

      setSaveError(localApiDetail(err) || toErrorString(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center px-2 ${A.body}`}
      >
        <div className="text-gray-500">{t("sales.edit.loading")}</div>
      </div>
    );

  if (loadError)
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center px-2 ${A.body}`}
      >
        <div className="text-red-600">{messageText(loadError)}</div>
      </div>
    );

  if (!sale || !form)
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center px-2 ${A.body}`}
      >
        <div className="text-gray-500">{t("sales.detail.notFound")}</div>
      </div>
    );

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <IconButton aria-label={t("directory.common.back")} onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </IconButton>

        <h2 className={`${A.title} text-center flex-1 tracking-tight`}>
          {t("sales.edit.title")}
        </h2>

        <IconButton
          aria-label={t("directory.common.close")}
          onClick={() => navigate(companyRoute(company_id!, "/sales"))}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </IconButton>
      </div>

      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl mx-auto"
        aria-busy={saving}
      >
        <AppleCard padding="p-5 sm:p-6" className={A.body}>
          {/* Info Header (consistent with SaleDetail) */}
          <SaleInfoHeader
          labels={{
            view: t("sales.header.view"),
            download: t("sales.header.download"),
            payLinkDraft: t("sales.header.payLinkDraft"),
            payLinkSent: t("sales.header.payLinkSent"),
            payLinkOpened: t("sales.header.payLinkOpened"),
            payLinkPaid: t("sales.header.payLinkPaid"),
            payLinkExpired: t("sales.header.payLinkExpired"),
            payLink: t("sales.header.payLink"),
            sending: t("sales.header.sending"),
            send: t("sales.header.send"),
            payLinkAction: t("sales.header.payLinkAction"),
            upgradePro: t("sales.header.upgradePro"),
            viewInvoice: t("sales.header.viewInvoice"),
            downloadInvoice: t("sales.header.downloadInvoice"),
            openPayLink: t("sales.header.openPayLink"),
            sendInvoice: t("sales.header.sendInvoice"),
            cannotSend: t("sales.header.cannotSend"),
            addCustomerDetails: t("sales.header.addCustomerDetails"),
            dueDate: t("sales.common.dueDate"), date: t("sales.common.date"), invoice: t("sales.common.invoice"), customer: t("sales.common.customer"), outstanding: t("sales.common.outstanding"), total: t("sales.common.total")
          }}
            date={form.date || sale.date}
            invoice={sale.invoice?.invoice_number ?? null}
            invoiceId={sale.invoice?.id ?? null}
            customer={selectedCustomer?.name ?? sale.customer?.name ?? null}
            email={selectedCustomer?.email ?? sale.customer?.email ?? null}
            statusBubbles={bubbles.map((bubble) => ({ ...bubble, id: bubble.bubbleType, label: ({
            paid: t("sales.detail.paid"), unpaid: t("sales.detail.unpaid"), voided: t("sales.detail.voided"),
            preorder: t("sales.detail.preorder"), fulfilled: t("sales.detail.fulfilled"), partial: t("sales.detail.partialPaid"),
          })[bubble.bubbleType] }))}
            edited={sale.edited && !sale.voided}
            lastEditedAt={sale.last_edited_at}
            outstanding={computedOutstanding}
            dueDate={isOnAccount ? form.due_date || sale.due_date : undefined}
            total={computedTotal}
            isPaid={false}
            showInvoiceLink={false}
          />

          {/* Customer / Payment Method */}
          <div className="mb-4 grid grid-cols-1 gap-4">
            <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
              label={t("sales.common.customer")}
              options={customers}
              value={selectedCustomer}
              onChange={(c) =>
                handleChange("customer_id", c ? String(c.id) : "")
              }
              getOptionLabel={(c) => c.name}
              placeholder={t("sales.edit.selectCustomer")}
              disabled={saving}
            />
          </div>

          {/* Due Date (on account only) */}
          {isOnAccount && (
            <div className="mb-4">
              <label className={`block mb-1 ${A.label}`}>{t("sales.common.dueDate")}</label>
              <AppleDateInput openCalendarLabel={t("sales.shared.openCalendar")}                 valueYmd={form.due_date ?? ""}
                onChangeYmd={(ymd) => {
                  // Mark the field dirty so an untouched save never repopulates
                  // a stored NULL, while a deliberate change/clear is honored.
                  dueDateDirtyRef.current = true;
                  handleChange("due_date", ymd);
                }}
                minYmd={form.date || undefined}
                disabled={saving}
                ariaLabel={t("sales.common.dueDateAria")}
                allowEmpty
              />
            </div>
          )}

          {/* Sale lines - DESKTOP */}
          <div className="mb-5 hidden md:block">
            <div className={`${A.label} mb-2`}>{t("sales.edit.items")}</div>

            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
              <table className="w-full text-[13px] text-gray-700">
                <thead className="bg-gray-50">
                  <tr className="text-xs text-gray-500">
                    <th className="text-left px-3 py-2 font-normal">
                      {t("sales.edit.itemService")}
                    </th>
                    <th className="text-right px-2 py-2 font-normal tabular-nums">
                      {t("sales.common.qty")}
                    </th>
                    <th className="text-right px-2 py-2 font-normal tabular-nums">
                      {t("sales.common.unitPrice")}
                    </th>
                    <th className="text-right px-2 py-2 font-normal tabular-nums">
                      {t("sales.edit.discountPercent")}
                    </th>
                    <th className="text-right px-2 py-2 font-normal tabular-nums">
                      {t("sales.edit.subtotal")}
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {form.lines.map((line: SaleLine, idx: number) => (
                    <tr key={idx}>
                      {/* Item/Service */}
                      <td className="px-3 py-2 align-top">
                        <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                          options={productOptions}
                          value={
                            productOptions.find(
                              (opt) =>
                                String(opt.id) === String(line.product_id),
                            ) ?? null
                          }
                          onChange={(opt) =>
                            handleLineProductChange(
                              idx,
                              opt ? String(opt.id) : "",
                            )
                          }
                          getOptionSearchText={(opt) => opt.name}
                      getOptionLabel={(opt) => opt.id === "" ? t("sales.entry.serviceOption") : typeof opt.stock !== "undefined" ? t("sales.edit.stockOption", { name: opt.rawName, stock: opt.stock }) : opt.rawName}
                          placeholder={t("sales.edit.selectItem")}
                          disabled={saving}
                        />

                        {line.item_kind === "service" && (
                          <AppleInput
                            type="text"
                            placeholder={t("sales.edit.description")}
                            value={line.detail ?? ""}
                            onChange={(e) =>
                              handleLineChange(idx, "detail", e.target.value)
                            }
                            className="mt-2"
                            disabled={saving}
                          />
                        )}
                      </td>

                      {/* Qty */}
                      <td className="px-2 py-2 align-top">
                        <AppleNumberInput
                          value={line.qty}
                          onChange={(e) =>
                            handleLineChange(idx, "qty", e.target.value)
                          }
                          onBlur={(e) => handleQtyBlur(idx, e.target.value)}
                          className="w-20 text-right"
                          disabled={saving}
                        />
                      </td>

                      {/* Unit Price */}
                      <td className="px-2 py-2 align-top">
                        <AppleNumberInput
                          value={line.price}
                          onChange={(e) =>
                            handleLineChange(idx, "price", e.target.value)
                          }
                          onBlur={(e) => handlePriceBlur(idx, e.target.value)}
                          className="w-24 text-right"
                          disabled={saving}
                        />
                      </td>

                      {/* Discount % */}
                      <td className="px-2 py-2 align-top">
                        <AppleNumberInput
                          value={line.discount}
                          onChange={(e) =>
                            handleLineChange(idx, "discount", e.target.value)
                          }
                          onBlur={(e) =>
                            handleDiscountBlur(idx, e.target.value)
                          }
                          className="w-20 text-right"
                          disabled={saving}
                        />
                      </td>

                      {/* Sub-total */}
                      <td className="px-2 py-2 align-top">
                        <AppleNumberInput
                          value={line.total}
                          onChange={(e) =>
                            handleLineChange(idx, "total", e.target.value)
                          }
                          onBlur={(e) => handleTotalBlur(idx, e.target.value)}
                          className="w-24 text-right"
                          disabled={saving}
                        />
                      </td>

                      {/* Remove */}
                      <td className="px-2 py-2 align-top text-right">
                        <IconButton
                          aria-label={t("sales.edit.removeLine")}
                          title={t("sales.edit.removeLine")}
                          onClick={() => handleLineRemove(idx)}
                          disabled={saving}
                          className="h-9 w-9 hover:bg-gray-50"
                        >
                          <TrashIcon className="w-5 h-5 text-gray-400" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sale lines - MOBILE */}
          <div className="md:hidden mb-5">
            <div className={`${A.label} mb-2`}>{t("sales.edit.items")}</div>

            <div className="space-y-2">
              {form.lines.map((line: SaleLine, idx: number) => (
                <AppleCard
                  key={idx}
                  padding="p-4"
                  className={`${A.body}`}
                >
                  <div className="mb-2">
                    <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                      options={productOptions}
                      value={
                        productOptions.find(
                          (opt) => String(opt.id) === String(line.product_id),
                        ) ?? null
                      }
                      onChange={(opt) =>
                        handleLineProductChange(idx, opt ? String(opt.id) : "")
                      }
                      getOptionSearchText={(opt) => opt.name}
                      getOptionLabel={(opt) => opt.id === "" ? t("sales.entry.serviceOption") : typeof opt.stock !== "undefined" ? t("sales.edit.stockOption", { name: opt.rawName, stock: opt.stock }) : opt.rawName}
                      placeholder={t("sales.edit.selectItem")}
                      disabled={saving}
                    />

                    {line.item_kind === "service" && (
                      <AppleInput
                        type="text"
                        placeholder={t("sales.edit.description")}
                        value={line.detail ?? ""}
                        onChange={(e) =>
                          handleLineChange(idx, "detail", e.target.value)
                        }
                        className="mt-2"
                        disabled={saving}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block mb-1 ${A.label}`}>{t("sales.common.qty")}</label>
                      <AppleNumberInput
                        value={line.qty}
                        onChange={(e) =>
                          handleLineChange(idx, "qty", e.target.value)
                        }
                        onBlur={(e) => handleQtyBlur(idx, e.target.value)}
                        className="text-right"
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <label className={`block mb-1 ${A.label}`}>
                        {t("sales.common.unitPrice")}
                      </label>
                      <AppleNumberInput
                        value={line.price}
                        onChange={(e) =>
                          handleLineChange(idx, "price", e.target.value)
                        }
                        onBlur={(e) => handlePriceBlur(idx, e.target.value)}
                        className="text-right"
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <label className={`block mb-1 ${A.label}`}>{t("sales.edit.discountPercent")}</label>
                      <AppleNumberInput
                        value={line.discount}
                        onChange={(e) =>
                          handleLineChange(idx, "discount", e.target.value)
                        }
                        onBlur={(e) => handleDiscountBlur(idx, e.target.value)}
                        className="text-right"
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <label className={`block mb-1 ${A.label}`}>
                        {t("sales.edit.subtotal")}
                      </label>
                      <AppleNumberInput
                        value={line.total}
                        onChange={(e) =>
                          handleLineChange(idx, "total", e.target.value)
                        }
                        onBlur={(e) => handleTotalBlur(idx, e.target.value)}
                        className="text-right"
                        disabled={saving}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-2">
                    <IconButton
                      aria-label={t("sales.edit.removeLine")}
                      title={t("sales.edit.removeLine")}
                      onClick={() => handleLineRemove(idx)}
                      disabled={saving}
                      className="h-9 w-9 hover:bg-gray-50"
                    >
                      <TrashIcon className="w-5 h-5 text-gray-400" />
                    </IconButton>
                  </div>
                </AppleCard>
              ))}
            </div>
          </div>

          {/* ✅ Memo (always editable on edit page; no pencil; saved only on Enter) */}
          <AppleCard padding="p-4" className="mb-6">
            <MemoSection
          editAriaLabel={t("sales.common.editMemo")}
              mode="edit"
              variant="plain"
              label={t("sales.common.memo")}
              value={memoDraft}
              onChange={setMemoDraft}
              rows={2}
              disabled={locked || saving}
              placeholder={t("sales.common.memoPlaceholder")}
            />
          </AppleCard>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8">
            <Button
              variant="secondary"
              type="button"
              minWidth="120px"
              onClick={() =>
                navigate(companyRoute(company_id!, `/sales/${id}`))
              }
              disabled={saving}
            >
              {t("directory.common.cancel")}
            </Button>

            <Button
              variant="primary"
              type="submit"
              minWidth="120px"
              disabled={saving}
            >
              {saving ? t("directory.common.savingEllipsis") : t("sales.common.enter")}
            </Button>
          </div>

          {/* Inline errors */}
          {saveError && (
            <div className="mt-4 text-center text-[13px] text-red-600 whitespace-pre-wrap">
              {messageText(saveError)}
            </div>
          )}
        </AppleCard>
      </form>
    </div>
  );
}
